// built-from: /activity-output-capture v1 impl:activity-output-capture role:implementation
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type ActivityOutputReviewItem = {
  id: string;
  activityId: string;
  name: string;
  method: string;
  state: 'ready-for-review' | 'changes-requested' | 'reviewed';
  revision: number;
  returnedAt: string;
  reviewer: { id: string; name: string; role?: string };
  contributors: ReadonlyArray<{ id: string; name: string; role?: string }>;
  sessionIds: ReadonlyArray<string>;
  summary: string;
  assumptions: ReadonlyArray<string>;
  openQuestions: ReadonlyArray<string>;
  flowText?: string;
};

type Props = {
  output: ActivityOutputReviewItem;
  versions: ReadonlyArray<ActivityOutputReviewItem>;
  onCorrectAtSource: () => void | Promise<void>;
  onMarkReviewed: () => void | Promise<void>;
  methodNativeOutput?: ReactNode;
  activityPath?: string;
  activityName?: string;
  busy?: boolean;
  error?: string | null;
  conflict?: boolean;
  onLoadLatest?: () => void | Promise<void>;
};

const stateLabel = {
  'ready-for-review': 'Ready for review',
  'changes-requested': 'Changes requested',
  reviewed: 'Reviewed',
} as const;

function returnedDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : date.toLocaleString('en-IE', { dateStyle: 'medium', timeStyle: 'short' });
}

export function ActivityOutputReview({
  output,
  versions,
  onCorrectAtSource,
  onMarkReviewed,
  methodNativeOutput,
  activityPath = '/engagement/discovery/activities/map-priority-value-streams',
  activityName = 'Map priority value streams',
  busy = false,
  error = null,
  conflict = false,
  onLoadLatest,
}: Props): JSX.Element {
  const reviewed = output.state === 'reviewed';
  const orderedVersions = [...versions].sort((left, right) => right.revision - left.revision);

  return (
    <div className="journey-page output-review-page">
      <nav className="journey-breadcrumb" aria-label="Breadcrumb">
        <Link to="/engagement/discovery">Discovery</Link>
        <span aria-hidden="true">/</span>
        <Link to={activityPath}>{activityName}</Link>
        <span aria-hidden="true">/</span>
        <span>Output Version {output.revision}</span>
      </nav>

      <header className="journey-page__header journey-page__header--action">
        <div>
          <div className="journey-title-line">
            <span className="journey-eyebrow">Activity output · Version {output.revision}</span>
            <span className={`journey-status journey-status--${reviewed ? 'complete' : 'awaiting-review'}`}>
              {stateLabel[output.state]}
            </span>
          </div>
          <h1>{output.name}</h1>
          <p>{output.summary}</p>
        </div>
        {!reviewed && !conflict ? (
          <div className="output-review-actions">
            <button
              className="journey-button journey-button--secondary"
              type="button"
              disabled={busy}
              onClick={() => void onCorrectAtSource()}
            >
              Correct at source
            </button>
            <button
              className="journey-button journey-button--primary journey-button--large"
              type="button"
              disabled={busy}
              onClick={() => void onMarkReviewed()}
            >
              {busy ? 'Saving…' : 'Mark reviewed'}
            </button>
          </div>
        ) : null}
      </header>

      {error ? (
        <section className="journey-output-notice journey-output-notice--error" role="alert">
          <span>{error}</span>
          {conflict && onLoadLatest ? (
            <button type="button" className="journey-button journey-button--secondary" onClick={() => void onLoadLatest()}>
              Load latest output
            </button>
          ) : null}
        </section>
      ) : null}

      {reviewed ? (
        <section className="journey-panel output-review-success" role="status">
          <strong>Eligible for later synthesis — not a finding</strong>
          <span>This reviewed output can be used by a later synthesis activity; no finding has been created.</span>
        </section>
      ) : (
        <section className="journey-panel output-review-boundary" aria-label="Review boundary">
          <strong>Review the returned activity output.</strong>
          <span>Corrections happen in the source VSM session. Marking it reviewed does not create a finding.</span>
        </section>
      )}

      <div className="output-review-grid">
        <main className="journey-panel output-review-content" aria-labelledby="output-content-title">
          <div className="journey-section-heading journey-section-heading--compact">
            <div>
              <span className="journey-eyebrow">Method-native output</span>
              <h2 id="output-content-title">Current-state value stream</h2>
            </div>
          </div>
          {methodNativeOutput ?? (
            <div className="output-review-summary">
              <strong>{output.summary}</strong>
              {output.flowText ? <pre className="output-review-flow">{output.flowText}</pre> : <span>Flow text: Not captured</span>}
            </div>
          )}
        </main>

        <aside className="output-review-aside">
          <section className="journey-panel" aria-labelledby="provenance-title">
            <span className="journey-eyebrow">Traceability</span>
            <h2 id="provenance-title">Source and review</h2>
            <dl className="journey-definition-list">
              <dt>Activity</dt><dd>{activityName}</dd>
              <dt>Method</dt><dd>{output.method}</dd>
              <dt>Source session</dt><dd>{output.sessionIds[0] ?? 'Not recorded'}</dd>
              <dt>Returned</dt><dd>{returnedDate(output.returnedAt)}</dd>
              <dt>Contributors</dt><dd>{output.contributors.map((person) => person.name).join(', ')}</dd>
              <dt>Reviewer</dt><dd>{output.reviewer.name}</dd>
            </dl>
          </section>

          <section className="journey-panel" aria-labelledby="assumptions-title">
            <span className="journey-eyebrow">Review context</span>
            <h2 id="assumptions-title">Assumptions</h2>
            <ul className="journey-check-list">
              {output.assumptions.length
                ? output.assumptions.map((item) => <li key={item}>{item}</li>)
                : <li>Not captured</li>}
            </ul>
          </section>

          <section className="journey-panel" aria-labelledby="questions-title">
            <span className="journey-eyebrow">Still unresolved</span>
            <h2 id="questions-title">Open questions</h2>
            <ul className="journey-check-list">
              {output.openQuestions.length
                ? output.openQuestions.map((item) => <li key={item}>{item}</li>)
                : <li>Not captured</li>}
            </ul>
          </section>

          <section className="journey-panel" aria-labelledby="versions-title">
            <span className="journey-eyebrow">History</span>
            <h2 id="versions-title">Versions</h2>
            <ol className="output-version-list">
              {orderedVersions.map((version) => (
                <li key={version.revision}>
                  <strong>Version {version.revision}</strong>
                  <span>{stateLabel[version.state]}{version.revision === output.revision ? ' · latest' : ''}</span>
                </li>
              ))}
            </ol>
          </section>
        </aside>
      </div>
    </div>
  );
}
